// components/TablePersonDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Slide,
  InputAdornment,
  Paper,
  Avatar,
  Chip,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Diamond as DiamondIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { formatPrice } from '../components/CarrancyTrans';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TablePersonDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  language,
  tableId,
  restId,
  maxPersons = 30,
  minPersons = 1,
  disableBackdropClose = true,
  disableEscapeKeyClose = true,
  currency = 'MYR'
}) => {
  const [personCount, setPersonCount] = useState(1);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [tableInfo, setTableInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Fetch table info when dialog opens
  useEffect(() => {
    if (open && tableId) {
      fetchTableInfo();
    }
  }, [open, tableId]);

  const fetchTableInfo = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await api.get(`/categories/table/${tableId}/rest/${restId}`);
      setTableInfo(response.data);
      
      // Update maxPersons based on table capacity
      if (response.data.capacity) {
        // You can update maxPersons here if needed
      }
    } catch (err) {
      console.error('Error fetching table info:', err);
      setFetchError(language === 'ar' 
        ? 'فشل في تحميل معلومات الطاولة'
        : 'Failed to load table information'
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setPersonCount(1);
      setError('');
      setActiveStep(0);
    }
  }, [open]);

  const handlePersonCountChange = (event) => {
    const value = parseInt(event.target.value);
    if (isNaN(value)) {
      setPersonCount(1);
      return;
    }
    
    if (value < minPersons) {
      setError(language === 'ar' 
        ? `الحد الأدنى لعدد الأشخاص هو ${minPersons}`
        : `Minimum number of persons is ${minPersons}`
      );
      setPersonCount(minPersons);
    } else if (value > maxPersons) {
      setError(language === 'ar' 
        ? `الحد الأقصى لعدد الأشخاص هو ${maxPersons}`
        : `Maximum number of persons is ${maxPersons}`
      );
      setPersonCount(maxPersons);
    } else {
      setError('');
      setPersonCount(value);
    }
  };

  const handleIncrement = () => {
    if (personCount < maxPersons) {
      setPersonCount(prev => prev + 1);
      setError('');
    } else {
      setError(language === 'ar' 
        ? `الحد الأقصى لعدد الأشخاص هو ${maxPersons}`
        : `Maximum number of persons is ${maxPersons}`
      );
    }
  };

  const handleDecrement = () => {
    if (personCount > minPersons) {
      setPersonCount(prev => prev - 1);
      setError('');
    } else {
      setError(language === 'ar' 
        ? `الحد الأدنى لعدد الأشخاص هو ${minPersons}`
        : `Minimum number of persons is ${minPersons}`
      );
    }
  };

  const handleConfirm = () => {
    if (personCount >= minPersons && personCount <= maxPersons) {
      onConfirm(personCount, tableInfo);
    } else {
      setError(language === 'ar' 
        ? `الرجاء إدخال عدد أشخاص صحيح (${minPersons}-${maxPersons})`
        : `Please enter a valid number of persons (${minPersons}-${maxPersons})`
      );
    }
  };

  const handleNext = () => {
    if (personCount >= minPersons && personCount <= maxPersons) {
      setActiveStep(1);
    } else {
      setError(language === 'ar' 
        ? `الرجاء إدخال عدد أشخاص صحيح (${minPersons}-${maxPersons})`
        : `Please enter a valid number of persons (${minPersons}-${maxPersons})`
      );
    }
  };

  const handleCloseAttempt = (event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyClose && reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const getPersonIcon = () => {
    if (personCount <= 2) return <PeopleIcon />;
    if (personCount <= 4) return <GroupIcon />;
    return <RestaurantIcon />;
  };

  const getPersonMessage = () => {
    if (personCount <= 2) {
      return language === 'ar' 
        ? 'ترحيب حار بكم! نتمنى لكم وقتاً ممتعاً'
        : 'Warm welcome! Wishing you a great time';
    }
    if (personCount <= 4) {
      return language === 'ar' 
        ? 'عائلة وأصدقاء! استمتعوا بوقتكم معنا'
        : 'Family and friends! Enjoy your time with us';
    }
    return language === 'ar' 
      ? 'مجموعة كبيرة! فريقنا جاهز لخدمتكم'
      : 'Large group! Our team is ready to serve you';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return formatPrice(amount, currency, language)    
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseAttempt}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={disableEscapeKeyClose}
      PaperProps={{
        sx: {
          backgroundColor: '#0D0D0D',
          borderRadius: '24px',
          border: tableInfo?.is_vip ? '2px solid #9C27B0' : '2px solid #FFD700',
          overflow: 'hidden',
          position: 'relative',
          background: tableInfo?.is_vip 
            ? 'linear-gradient(135deg, #0D0D0D 0%, #1A0D1A 100%)'
            : 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        }
      }}
    >
      {/* Header with Stepper */}
      <Box sx={{ px: 3, pt: 4, pb: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel 
              StepIconProps={{
                sx: {
                  '&.Mui-active': { color: '#FFD700' },
                  '&.Mui-completed': { color: '#4CAF50' },
                  color: '#2D2D2D',
                }
              }}
            >
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'عدد الأشخاص' : 'Number of Persons'}
              </Typography>
            </StepLabel>
          </Step>
          <Step>
            <StepLabel 
              StepIconProps={{
                sx: {
                  '&.Mui-active': { color: '#FFD700' },
                  '&.Mui-completed': { color: '#4CAF50' },
                  color: '#2D2D2D',
                }
              }}
            >
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'تأكيد' : 'Confirm'}
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={100} sx={{ bgcolor: '#1A1A1A', borderRadius: '16px', mb: 3 }} />
            <Skeleton variant="circular" width={60} height={60} sx={{ bgcolor: '#1A1A1A', mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" sx={{ bgcolor: '#1A1A1A', mx: 'auto', width: '60%' }} />
          </Box>
        ) : fetchError ? (
          <Alert severity="error" sx={{ backgroundColor: 'rgba(255, 61, 0, 0.1)', color: '#FF3D00' }}>
            {fetchError}
          </Alert>
        ) : activeStep === 0 ? (
          <Fade in={activeStep === 0} timeout={300}>
            <Box>
              {/* Table Info Card */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: tableInfo?.is_vip ? '#1A0D1A' : '#1A1A1A',
                  borderRadius: '16px',
                  p: 2,
                  mb: 3,
                  textAlign: 'center',
                  border: tableInfo?.is_vip ? '1px solid #9C27B0' : '1px solid #2D2D2D',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* VIP Badge */}
                {tableInfo?.is_vip && (
                  <Chip
                    icon={<DiamondIcon />}
                    label={language === 'ar' ? 'VIP' : 'VIP'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: '#9C27B0',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      '& .MuiChip-icon': {
                        color: '#FFD700',
                      }
                    }}
                  />
                )}

                <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 0.5 }}>
                  {language === 'ar' ? 'رقم الطاولة' : 'Table Number'}
                </Typography>
                <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 800 }}>
                  {tableInfo?.table_number || tableId || '--'}
                </Typography>
                
                {/* Table Name */}
                {tableInfo?.table_name && (
                  <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 0.5 }}>
                    {tableInfo.table_name}
                  </Typography>
                )}

                {/* Location */}
                {tableInfo?.location && (
                  <Typography variant="caption" sx={{ color: '#6B6B6B', display: 'block', mt: 1 }}>
                    {language === 'ar' ? 'الموقع: ' : 'Location: '}
                    {tableInfo.location}
                  </Typography>
                )}

                {/* Capacity */}
                {tableInfo?.capacity && (
                  <Typography variant="caption" sx={{ color: '#6B6B6B', display: 'block' }}>
                    {language === 'ar' ? 'السعة: ' : 'Capacity: '}
                    {tableInfo.capacity} {language === 'ar' ? 'أشخاص' : 'persons'}
                  </Typography>
                )}
              </Paper>

              {/* Minimum Charge Alert for VIP Tables */}
              {tableInfo?.is_vip && tableInfo?.min_charge > 0 && (
                <Alert
                  severity="warning"
                  icon={<AttachMoneyIcon />}
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    color: '#CE93D8',
                    border: '1px solid #9C27B0',
                    '& .MuiAlert-icon': {
                      color: '#9C27B0',
                    }
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {language === 'ar' 
                      ? `الحد الأدنى للطلب: ${formatCurrency(tableInfo.min_charge)}`
                      : `Minimum order amount: ${formatCurrency(tableInfo.min_charge)}`
                    }
                  </Typography>
                </Alert>
              )}

              {/* Person Count Input */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#FFD700', 
                    fontWeight: 700,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  {getPersonIcon()}
                  {language === 'ar' ? 'عدد الأشخاص' : 'Number of Persons'}
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 2,
                  mb: 2
                }}>
                  <IconButton
                    onClick={handleDecrement}
                    disabled={personCount <= minPersons}
                    sx={{
                      backgroundColor: '#1A1A1A',
                      border: '1px solid #2D2D2D',
                      color: '#FFD700',
                      width: 48,
                      height: 48,
                      '&:hover': {
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        borderColor: '#FFD700',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#0D0D0D',
                        color: '#6B6B6B',
                        borderColor: '#2D2D2D',
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>

                  <TextField
                    value={personCount}
                    onChange={handlePersonCountChange}
                    type="number"
                    inputProps={{ 
                      min: minPersons, 
                      max: maxPersons,
                      style: { 
                        textAlign: 'center',
                        fontSize: '2rem',
                        fontWeight: 800,
                        padding: '8px 0',
                      }
                    }}
                    sx={{
                      width: 120,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1A1A1A',
                        borderRadius: '16px',
                        '& fieldset': {
                          borderColor: tableInfo?.is_vip ? '#9C27B0' : '#FFD700',
                          borderWidth: '2px',
                        },
                        '&:hover fieldset': {
                          borderColor: tableInfo?.is_vip ? '#BA68C8' : '#FFE55C',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: tableInfo?.is_vip ? '#9C27B0' : '#FFD700',
                          borderWidth: '2px',
                        },
                      },
                      '& input': {
                        color: tableInfo?.is_vip ? '#CE93D8' : '#FFD700',
                        fontSize: '2rem',
                        fontWeight: 800,
                        textAlign: 'center',
                      },
                    }}
                  />

                  <IconButton
                    onClick={handleIncrement}
                    disabled={personCount >= maxPersons}
                    sx={{
                      backgroundColor: '#1A1A1A',
                      border: '1px solid #2D2D2D',
                      color: '#FFD700',
                      width: 48,
                      height: 48,
                      '&:hover': {
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        borderColor: '#FFD700',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#0D0D0D',
                        color: '#6B6B6B',
                        borderColor: '#2D2D2D',
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {/* Quick select chips */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 1, 
                  flexWrap: 'wrap',
                  mt: 2
                }}>
                  {[2, 4, 6, 8].map(num => (
                    <Button
                      key={num}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setPersonCount(num);
                        setError('');
                      }}
                      sx={{
                        borderColor: personCount === num ? (tableInfo?.is_vip ? '#9C27B0' : '#FFD700') : '#2D2D2D',
                        color: personCount === num ? (tableInfo?.is_vip ? '#CE93D8' : '#FFD700') : '#B3B3B3',
                        borderRadius: '20px',
                        minWidth: 50,
                        '&:hover': {
                          borderColor: tableInfo?.is_vip ? '#9C27B0' : '#FFD700',
                          backgroundColor: `rgba(${tableInfo?.is_vip ? '156, 39, 176' : '255, 215, 0'}, 0.1)`,
                        }
                      }}
                    >
                      {num}
                    </Button>
                  ))}
                </Box>
              </Box>

              {error && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#FF3D00', 
                    textAlign: 'center',
                    display: 'block',
                    mt: 1
                  }}
                >
                  {error}
                </Typography>
              )}

              {/* Person count message */}
              <Fade in={true} timeout={500}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: tableInfo?.is_vip ? 'rgba(156, 39, 176, 0.05)' : 'rgba(255, 215, 0, 0.05)',
                    borderRadius: '12px',
                    p: 2,
                    mt: 2,
                    textAlign: 'center',
                    border: `1px solid ${tableInfo?.is_vip ? 'rgba(156, 39, 176, 0.2)' : 'rgba(255, 215, 0, 0.2)'}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#B3B3B3', fontStyle: 'italic' }}>
                    {getPersonMessage()}
                  </Typography>
                </Paper>
              </Fade>
            </Box>
          </Fade>
        ) : (
          <Fade in={activeStep === 1} timeout={300}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: tableInfo?.is_vip ? '#9C27B0' : '#FFD700',
                  color: '#000000',
                  margin: '0 auto 16px',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48 }} />
              </Avatar>
              
              <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 800, mb: 1 }}>
                {language === 'ar' ? 'تم التأكيد!' : 'Confirmed!'}
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 0.5 }}>
                {language === 'ar' 
                  ? `تم تأكيد ${personCount} أشخاص على الطاولة رقم ${tableInfo?.table_number || tableId}`
                  : `${personCount} person(s) confirmed for table ${tableInfo?.table_number || tableId}`
                }
              </Typography>

              {tableInfo?.is_vip && tableInfo?.min_charge > 0 && (
                <Alert
                  severity="info"
                  icon={<InfoIcon />}
                  sx={{
                    mt: 2,
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    color: '#CE93D8',
                    border: '1px solid #9C27B0',
                    '& .MuiAlert-icon': {
                      color: '#9C27B0',
                    }
                  }}
                >
                  <Typography variant="body2">
                    {language === 'ar' 
                      ? `الحد الأدنى للطلب لهذه الطاولة هو ${formatCurrency(tableInfo.min_charge)}`
                      : `Minimum order amount for this table is ${formatCurrency(tableInfo.min_charge)}`
                    }
                  </Typography>
                </Alert>
              )}
              
              <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 2 }}>
                {language === 'ar' 
                  ? 'يمكنك الآن البدء في طلب وجباتك'
                  : 'You can now start ordering your meals'
                }
              </Typography>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
        {activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            fullWidth
            disabled={personCount < minPersons || personCount > maxPersons || loading}
            sx={{
              backgroundColor: tableInfo?.is_vip ? '#9C27B0' : '#FFD700',
              color: '#FFFFFF',
              borderRadius: '12px',
              py: 1.2,
              fontWeight: 700,
              '&:hover': {
                backgroundColor: tableInfo?.is_vip ? '#BA68C8' : '#FFE55C',
                color: '#000000',
              },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B',
              }
            }}
          >
            {language === 'ar' ? 'متابعة' : 'Continue'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: tableInfo?.is_vip ? '#9C27B0' : '#4CAF50',
              color: '#FFFFFF',
              borderRadius: '12px',
              py: 1.2,
              fontWeight: 700,
              '&:hover': {
                backgroundColor: tableInfo?.is_vip ? '#BA68C8' : '#45a049',
              }
            }}
          >
            {language === 'ar' ? 'تأكيد وبدء الطلب' : 'Confirm & Start Order'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TablePersonDialog;